import {
  ArrowRight,
  Award,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

const Login = () => {
  const { login, loading } = useAffiliateUserStore();
  const navigate = useNavigate();

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Field validation states
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Validation functions
  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    if (touchedFields.email) newErrors.email = validateEmail(email);
    if (touchedFields.password) newErrors.password = validatePassword(password);
    setErrors(newErrors);
  }, [email, password, touchedFields]);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    // Mark all fields as touched for validation
    const allTouched = { email: true, password: true };
    setTouchedFields(allTouched);

    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError || passwordError) {
      setErrors({
        email: emailError,
        password: passwordError,
      });
      Error('Please fix the form errors before submitting');
      return;
    }

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        password,
      };

      const response = await login(payload);

      if (response?.success) {
        Success(`Welcome back, ${response.user?.name || 'Partner'}!`);
        navigate('/dashboard');
      } else {
        Error('Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      Error(
        error?.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const isFormValid = !validateEmail(email) && !validatePassword(password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-green-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex">
        {/* Left Side - Info Panel */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white">
          <div className="max-w-md">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mr-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Partner Program
              </h1>
            </div>

            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Welcome Back to Your
              <span className="block bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Success Journey
              </span>
            </h2>

            <p className="text-lg text-gray-300 mb-8">
              Continue building your affiliate empire and track your growing
              commissions with our advanced partner dashboard.
            </p>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Real-time earnings tracking
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Users className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Advanced referral analytics
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Award className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">Premium partner benefits</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome Back
              </h1>
              <p className="text-gray-400">Sign in to your partner account</p>
            </div>

            {/* Form Card */}
            <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
              <div className="hidden lg:block text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Sign In</h2>
                <p className="text-gray-400">Access your partner dashboard</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <FlexibleInput
                    placeholder="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => handleBlur('email')}
                    onKeyPress={handleKeyPress}
                    icon={Mail}
                    iconClass="text-green-400"
                    error={errors.email}
                    touched={touchedFields.email}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
                  />

                  <div className="relative">
                    <FlexibleInput
                      placeholder="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => handleBlur('password')}
                      onKeyPress={handleKeyPress}
                      icon={Lock}
                      iconClass="text-green-400"
                      error={errors.password}
                      touched={touchedFields.password}
                      className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 pr-12"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-4 text-gray-400 hover:text-white transition-colors z-10"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 bg-gray-800/50 border-gray-600 rounded"
                    />
                    <label
                      htmlFor="remember-me"
                      className="ml-2 block text-sm text-gray-300"
                    >
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm">
                    <Link
                      to="/auth/forgot-password"
                      className="text-green-400 hover:text-green-300 font-medium transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Button
                  text="Sign In"
                  onClick={handleLogin}
                  loading={loading}
                  disabled={!isFormValid}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                  size="large"
                  icon={ArrowRight}
                  iconPosition="right"
                />

                <div className="text-center">
                  <p className="text-gray-400">
                    Don't have an account?{' '}
                    <Link
                      to="/auth/register"
                      className="text-green-400 hover:text-green-300 font-medium transition-colors"
                    >
                      Create Account
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-xs text-gray-500">
                Secure login powered by advanced encryption
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
